import {
    ApiResponse,
    ClaimTransmissionStatusType,
    ClaimType,
    ICustomFilter,
    ITableColumn,
    ListProviderClaimsRequest,
    ListProviderPayersRequest,
    parseSorting2,
    ProviderClaim,
    ProviderReportItem,
    ReportLabelType,
    SpanType,
} from '@enter/core';
import { IChartHierarchy, IChartPoint, IChartTimeline, IPayerMixChart } from '../components';
import * as d3 from 'd3';
import _ from 'lodash';
import { SortingRule } from 'react-table';
import { IItemListingRequest, IItemSortOrder } from './root.store';

// TODO: split into module, with separate files
/**
 * `ms` is required. It used to default to a random delay, which no caller ever reached — all 63
 * call sites pass an explicit value — while making this module import the demo PRNG. `util.ts` is
 * imported by 42 files including the support console, so a dead branch was pulling demo fixtures
 * into a bundle that can never run them.
 */
export const waitForNoReason = (ms: number): Promise<void> => {
    return new Promise((resolve): void => {
        setTimeout(resolve, ms);
    });
};

export const stream2string = async (readableStream: ReadableStream): Promise<string> => {
    const reader = readableStream.getReader();
    const chunks: string[] = [];
    function pump(): Promise<string[]> {
        return reader.read().then(({ value, done }) => {
            if (done) {
                return chunks;
            }
            const s: string[] = _.map(value, (v: number): string => String.fromCharCode(v));
            chunks.push(s.join(''));
            return pump();
        });
    }
    await pump();
    return chunks.join('');
};

export const handleError = async (err: any): Promise<any> => {
    if (err && err.body && !err.bodyUsed) {
        const str: string = await stream2string(err.body);
        const msg = JSON.parse(str);
        // tslint:disable-next-line: no-console
        console.error(msg);
        err.payload = msg;
    } else {
        // NB: ignore aborted requests
        if (_.get(err, 'name') !== 'AbortError') {
            if (_.has(err, 'stack')) {
                // tslint:disable-next-line: no-console
                console.warn(_.get(err, 'stack'));
            } else {
                // tslint:disable-next-line: no-console
                console.warn(err);
            }
        }
    }
    return err;
};

// safely handle ApiResponse transformation
export async function parseResponse<T>(res: ApiResponse<T>): Promise<T> {
    try {
        return await res.value();
    } catch (err) {
        await handleError(err);
        throw err;
    }
}

// retrieve single Model
export async function fetch<S, T>(req?: S, fn?: (req: S) => Promise<T | undefined>): Promise<T | undefined> {
    try {
        if (req && fn) {
            return await fn(req);
        }
    } catch (err) {
        await handleError(err);
        throw err;
    }
}

// retrieve multiple Models based on provided IDs
export async function fetchAll<T>(
    ids?: (string | undefined)[],
    fn?: (id: string) => Promise<T | undefined>,
): Promise<T[] | undefined> {
    try {
        if (ids && ids.length && fn) {
            const promises = _.compact(
                ids.map((id: string | undefined) => {
                    return id ? fn(id) : undefined;
                }),
            );
            return _.compact<T>(await Promise.all(promises));
        }
    } catch (err) {
        await handleError(err);
        throw err;
    }
}

// retrieve multiple Models based on provided reqs
export async function listAll<T>(reqs: object[], fn: (id: object) => Promise<T | undefined>): Promise<T[] | undefined> {
    try {
        if (!_.isEmpty(reqs)) {
            const promises = reqs.map((req: object) => fn(req));
            return _.compact<T>(await Promise.all(promises));
        }
    } catch (err) {
        await handleError(err);
        throw err;
    }
}

const datePattern = /\d{4}-\d{2}-\d{2}/;
const dateTimePattern = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d*Z/;
const emailPattern = /^\S+@\S+\.\S+$/;
const numberPattern = /^\d+$/i;
const uuidPattern = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
export const isDate = (term: string): boolean => {
    return datePattern.exec(term) !== null;
};
export const isDateTime = (term: string): boolean => {
    return dateTimePattern.exec(term) !== null;
};
export const isEmail = (term: string): boolean => {
    return emailPattern.exec(term) !== null;
};
export const isNumber = (term: string): boolean => {
    return numberPattern.exec(term) !== null;
};
export const isUuid = (term: string): boolean => {
    return uuidPattern.exec(term) !== null;
};
export const firstAndLastFilter = (term?: string): { filterFirst?: string[]; filterLast?: string[] }[] => {
    // term 'abc' should result in 'filterFirst: abc OR filterLast: abc', which is 2 requests
    // term 'abc def' should result in  'filterFirst: abc AND filterLast: def', in 1 request
    if (term) {
        const parts = _.compact(_.split(term, ' '));
        if (parts.length === 1) {
            return [
                {
                    filterFirst: likeFilter(parts[0]),
                },
                {
                    filterLast: likeFilter(parts[0]),
                },
            ];
        }
        return [
            {
                filterFirst: likeFilter(_.head(parts)),
                filterLast: likeFilter(_.tail(parts)),
            },
        ];
    }
    return [];
};
export const accountNumberFilter = (term?: string): { filterAccountNumber?: string[] }[] => {
    if (!term) {
        return [];
    }

    return [
        {
            filterAccountNumber: equalsFilter(term),
        },
    ];
};
export const likeFilter = (term?: string | string[]): string[] | undefined => {
    if (_.isArray(term)) {
        return [_.map(term, d => `LK:${d}`).join(',')];
    } else if (_.isString(term)) {
        return [`LK:${term}`];
    }
};
export const equalsFilter = (term?: string | string[]): string[] | undefined => {
    if (_.isArray(term)) {
        return [_.map(term, d => `EQ:${d}`).join(',')];
    } else if (_.isString(term)) {
        return [`EQ:${term}`];
    }
};
export const betweenFilter = (date1: Date, date2: Date): string[] | undefined => {
    if (date1 && date2) {
        const startDay = d3.utcDay.floor(date1).toISOString();
        const endDay = d3.utcDay.ceil(date2).toISOString();
        return [`BT:${startDay}:${endDay}`];
    }
};
export const parseBetweenFilter = (filters: string[]): Date[] | undefined => {
    if (filters) {
        // ignore multiples for now
        const f = _.first(filters);
        // expect "BT:2019-02-22T00:00:00.000Z:2019-08-20T00:00:00.000Z"
        const re = new RegExp(`BT:(${dateTimePattern.source}):(${dateTimePattern.source})`);
        if (f && re.test(f)) {
            const matches = re.exec(f);
            const dates = _.slice(matches, 1);
            return _.map(dates, d => new Date(d));
        }
    }
};
export const betweenDayBoundsFilter = (date?: string): string[] | undefined => {
    if (date) {
        const day = new Date(date);
        const startOfDay = d3.utcDay.floor(day).toISOString();
        const endOfDay = d3.utcDay.offset(d3.utcSecond.offset(day), 1).toISOString();
        return [`BT:${startOfDay}:${endOfDay}`];
    }
};
export function appendTo<T>(array: T[], model?: T | T[], key?: string): T[] {
    if (model) {
        // Put new items first so they override existing items with same key
        return _.unionBy(_.concat([], model), array, key || 'id');
    } else {
        return array;
    }
}
export function replaceInArray<T, K>(array: T[], model: T | T[] | undefined, getKey: (x: T) => K): T[] {
    if (!model) {
        return array;
    }

    // Handle replacing multiple items at once
    if (Array.isArray(model)) {
        if (model.length === 0) {
            return array;
        }

        const replacementMap = new Map<K, T>();
        model.forEach(m => {
            replacementMap.set(getKey(m), m);
        });

        return array.map(item => {
            const key = getKey(item);
            const replacement = replacementMap.get(key);
            return replacement !== undefined ? replacement : item;
        });
    }

    // Single item replacement
    const key = getKey(model);
    const idx = array.findIndex(item => getKey(item) === key);

    if (idx === -1) {
        // If it doesn't exist, return original array unchanged
        return array;
    }

    const next = array.slice();
    next[idx] = model;
    return next;
}
export const getInterval = (span: SpanType): d3.CountableTimeInterval => {
    let interval: d3.CountableTimeInterval;
    switch (span) {
        case SpanType.MONTH:
            interval = d3.utcMonth;
            break;
        case SpanType.WEEKOFYEAR:
            interval = d3.utcWeek;
            break;
        case SpanType.DAYOFYEAR:
            interval = d3.utcDay;
            break;
        default:
            throw new Error(`Unknown SpanType '${span}'`);
    }
    return interval;
};

export const buildCacheKey = (obj?: object): string => {
    const pairs = _.map(_.keys(obj), key => `${key}=${_.get(obj, key)}`);
    return _.join(pairs, '|');
};

export const parseSortBy = <D extends object>(
    sortBy: SortingRule<object>[],
    columns: ITableColumn<D>[],
): IItemListingRequest => {
    // convert all values of key 'order' to type IItemSortOrder
    const { order, ...rest } = parseSorting2(sortBy, columns);
    return {
        ...rest,
        order: _.map(order, d => (d === 'ASCENDING' ? IItemSortOrder.ASCENDING : IItemSortOrder.DESCENDING)),
    };
};

export function parseItemListingRequest(
    ilr: IItemListingRequest,
): {
    limit?: number;
    offset?: number;
    sort?: string[];
    order?: ('ASCENDING' | 'DESCENDING')[];
} {
    const limit = ilr.pageSize;
    const page = ilr.page;
    const offset = !_.isNil(limit) && page ? page * limit : undefined; // NB: omit offset for page 0
    return {
        limit,
        offset,
        sort: ilr.sort,
        order: ilr.order,
    };
}

export const parseSortOrders = (orders: string[] | undefined): string[] | undefined => {
    if (_.isUndefined(orders)) {
        return undefined;
    }
    return _.map(orders, order => {
        switch (order) {
            case 'asc':
                return 'ASCENDING';
            case 'desc':
                return 'DESCENDING';
            default:
                throw new Error(`Unknown SortOrder value: ${order}`);
        }
    });
};

/**
 * Payer names for generated demo data — INVENTED, not real brands.
 *
 * This was a list of real payers (Kaiser, BCBS, UHC, Aetna, Cigna, Anthem, Optum, Magellan).
 * Showing those to a prospect implies contract relationships we may not have, to someone who may
 * be evaluating a competitor. Invented names cost a little recognisability and carry none of that.
 *
 * Also no longer `_.shuffle`d. That ran at MODULE LOAD using `Math.random`, so the payer order
 * changed on every page load no matter what the generators seeded — the demo dataset could not be
 * stable while this was the first thing to run (NS-15).
 */

// convert from ProviderReportItem[] to IChartTimeline
export const pri2ict = (
    startDate: Date,
    endDate: Date,
    claimTypes: ClaimType[],
    span: SpanType,
    items: ProviderReportItem[],
    labels: ReportLabelType[],
): IChartTimeline => {
    // filter out each unique ReportLabelType, based on provided labels
    const typeMap: Map<ReportLabelType, ProviderReportItem[]> = new Map();
    items.forEach(item => {
        if (item && item.label && _.includes(labels, item.label)) {
            let values = typeMap.get(item.label);
            if (!values) {
                values = [];
                typeMap.set(item.label, values);
            }
            values.push(item);
        }
    });

    const interval = getInterval(span);
    const preallocate = (): IChartPoint[] => {
        const range = interval.range(interval.floor(startDate), interval.ceil(endDate));
        return _.map(range, (date: Date) => ({
            date,
            amount: 0,
        }));
    };

    // preallocate dataMap based on expected labels
    const dataMap = _.transform(
        labels,
        (result: Map<ReportLabelType, IChartPoint[]>, label: ReportLabelType): void => {
            result.set(label, preallocate());
        },
        new Map<ReportLabelType, IChartPoint[]>(),
    );

    // populate IChartPoint arrays for each received type
    typeMap.forEach((typeItems: ProviderReportItem[], k: ReportLabelType): void => {
        // for each item array, pre-allocate the proper period sized data array
        const points = preallocate();

        // for each ProviderReportItem find the matching point and update it
        typeItems.forEach((item: ProviderReportItem): void => {
            try {
                if (item.group) {
                    const [year, intervalOffset] = item.group.split('-'); // For DAYOFYEAR span it is year-day
                    const point: IChartPoint | undefined = _.find(points, p => {
                        // find offset of the date interval, relative to its year
                        const startOfYear = d3.utcYear.floor(p.date);
                        const offset = interval.count(startOfYear, p.date);
                        return (
                            p.date.getUTCFullYear() === Number.parseInt(year, 10) &&
                            offset === Number.parseInt(intervalOffset, 10) - 1
                        );
                    });
                    if (point && item.amount && item.amount.amount) {
                        point.amount = Number.parseFloat(item.amount.amount);
                    }
                }
            } catch (err) {
                // tslint:disable-next-line: no-console
                console.error('Failed to parse', err);
            }
        });
        dataMap.set(k, points);
    });
    const data = _.map(_.entries(dataMap), '1');
    return {
        startDate,
        endDate,
        claimTypes,
        data,
    };
};

export const pri2ipmc = (
    startDate: Date,
    endDate: Date,
    claimTypes: ClaimType[],
    items: ProviderReportItem[],
): IPayerMixChart => {
    const allPayers = _.map(_.flatten(items), item => ({
        name: _.get(item, 'group', '') as string,
        amount: Number.parseFloat(_.get(item, 'amount.amount', '0')),
        parent: 'total',
    }));
    const merged = _.transform(
        allPayers,
        (result, payer) => {
            const key = _.replace(payer.name, /\W/gi, '');
            if (!_.has(result, key)) {
                _.set(result, key, payer);
            } else {
                const prior = _.get(result, key) as IChartHierarchy;
                prior.amount += payer.amount;
            }
        },
        {},
    );
    const mergedPayers: IChartHierarchy[] = _.values(merged);
    const payers = _.take(_.orderBy(mergedPayers, 'amount', 'desc'), 10);
    if (!_.isEmpty(payers)) {
        payers.push({
            name: 'total',
            parent: '',
            amount: 0,
        });
    }
    return {
        startDate,
        endDate,
        claimTypes,
        payers,
    };
};

// Convert from ICustomFilter to ListProviderPayersRequest
export const icf2lppr = (filters: ICustomFilter[]): ListProviderPayersRequest => {
    return _.transform(
        filters,
        (lpcr, filter) => {
            if (filter && filter.filterId) {
                const values = _.get(lpcr, filter.filterId, []);
                values.push(`${filter.filterMethod}:${filter.value}`);
                _.set(lpcr, filter.filterId, values);
            }
        },
        {},
    );
};

// Convert from ICustomFilter to ListProviderClaimsRequest
export const icf2lpcr = (filters: ICustomFilter[]): ListProviderClaimsRequest => {
    return _.transform(
        filters,
        (lpcr, filter) => {
            if (filter && filter.filterId) {
                const values = _.get(lpcr, filter.filterId, []);
                values.push(`${filter.filterMethod}:${filter.value}`);
                _.set(lpcr, filter.filterId, values);
            }
        },
        {},
    );
};

interface IFetchListResponse<T> {
    value: T[];
    limit: number;
    offset: number;
    total: number;
}

export async function parseFetchListRaw<T = any>(raw: ApiResponse<T[]>): Promise<IFetchListResponse<T>> {
    const value = await raw.value();
    const limit = await Number(raw.raw.headers.get('pagination-limit'));
    const offset = await Number(raw.raw.headers.get('pagination-offset'));
    const total = await Number(raw.raw.headers.get('pagination-total'));

    return { value, limit, offset, total };
}

export function updateClaimStatus(claim: ProviderClaim): ProviderClaim {
    // NB: update status to ADJUDICATED if payments exist.
    if (claim.transmissionStatus === ClaimTransmissionStatusType.REJECTED) {
        return claim;
    }

    if ((claim.payments || 0) > 0) {
        claim.transmissionStatus = ClaimTransmissionStatusType.ADJUDICATED;
    }
    return claim;
}

export function updateClaimsStatus(claims: ProviderClaim[]): ProviderClaim[] {
    return claims.map(claim => {
        if (claim.transmissionStatus === ClaimTransmissionStatusType.REJECTED) {
            return claim;
        }

        // NB: update status to ADJUDICATED if payments exist.
        if ((claim.payments || 0) > 0) {
            claim.transmissionStatus = ClaimTransmissionStatusType.ADJUDICATED;
        }
        return claim;
    });
}
